/**
 * @file This file provides a function that generically analyzes the config for a page of a FE v2 app and adapts the manifest artifacts defining the page accordingly (function exportPage).
 * This process is also called 'export'.
 * Thereby, the page specific schema for this page is heavily used. It is assumed that this schema was generated using
 * the tools provided in file ../generate/schemaAdaptation.ts.
 * Moreover, it is assumed that the config was originally created by ../import/importPage.ts and then modified (e.g. by Fiori Tools) based on the page specific schema.
 * Unit tests for this class are located in test.unit.genericSchemaHandling.exportPageGeneric.test.ts.
 */
import type {
    AccessorPath,
    AccessorSpec,
    ConversionExitProvider,
    ExtensionLogger,
    FileData,
    FileDataResult,
    Manifest,
    ManifestSettingsType
} from '@sap/ux-specification-types';
import { ArtifactType, ChangeIndicator, SchemaTag } from '@sap/ux-specification-types';
import type { Definition } from 'typescript-json-schema';
import { executeConversionExit, isDefinition, isEmpty } from '../utils';
import { getRefType, jsonPathToArray } from '../../common';

/**
 * An instance of this type collects all information needed for function exportPage.
 *
 * @property appId - id of the app. Note that this should not be taken from the manifest, since the manifest may still contain a placeholder.
 * @property config - the config to be exported. Note: This instance has the same structure as the config returned by function getConfigForPage in importPage.ts.
 * However, the instance has gone through a process of serialization and deserialization, so that it does not contain the corresponding type information.
 * @property manifest - manifest of the FE v2 app
 * @property jsonSchema - the page specific schema for config
 * @property conversionExitProvider - provider for conversion exits assigned in jsonSchema
 * @property fragments - fragments in status before the export has started
 * @property logger - used for logging any problems that occur during the export
 */
export type ExportPageInParameters = {
    appId: string;
    config: object;
    manifest: Manifest;
    jsonSchema: Definition;
    conversionExitProvider?: ConversionExitProvider;
    fragments?: FileData[];
    views: FileData[];
    logger?: ExtensionLogger;
};

/**
 * An instance of this type represents the information from a flex change that is relevant for the export process.
 *
 * @property controlId - the id of the control the flex change is defined for
 * @property controlType - the type of the control the flex change is defined for
 * @property propertyName - the name of the control property the flex change is defined for
 * @property isBinding - indicates whether the change sets a binding for the specified control property (in contrast to a fixed value)
 * @property newValue - the value (or binding) that is set for the specified control property. Note that a value of null
 * has a special meaning: It indicates that this control property should not have a flex change at all (thus an existing one should be deleted).
 */
export type FlexInfo = {
    controlId: string;
    controlType: string;
    propertyName: string;
    isBinding: boolean;
    newValue: ManifestSettingsType | null;
};

/**
 * An instance of this type represents the information delivered by function exportPage.
 *
 * @property manifest - the modified manifest
 * @property manifestChangeIndicator - information whether an update of the manifest has happened
 * @property flexList - flex info for all control properties that potentially can be modified via a flex change
 * @property fragments - fragments after the export including the relevant change information
 */
export type ExportPageResults = {
    manifest: Manifest;
    manifestChangeIndicator: ChangeIndicator;
    flexList: FlexInfo[];
    fragments: FileDataResult[];
    views: FileDataResult[];
};

/**
 * An instance of this type collects the global information being used within the export process.
 *
 * @property globalTypeDefinitions - global type definitions that may be referred from within any Definition in the page specific schema
 * @property fragments - fragments the list of fragments of the app. Maybe modified during the process.
 * @property conversionExitProvider - provider for conversion exits assigned in the page specific schema
 */
type GlobalInfoForExport = {
    globalTypeDefinitions: { [key: string]: Definition };
    fragments: FileDataResult[];
    conversionExitProvider?: ConversionExitProvider;
};

/**
 * This function is called for every property in the page specific schema with artifactType FlexChange.
 * Thereby, we assume that such a property always corresponds to a control property (with same name) that can be changed via a flex change
 * The function will derive the information about the flex change that should be created (or the information that no flex change is needed)
 * for this property and add it to the list.
 * Note: Currently we assume that conversion exits are only used for manifest based properties. Hence, no extra logic for conversion exits is needed here.
 *
 * @param propertyDefinition - definition of the property in the page specific schema
 * @param value - the value for the property according to the config object that is currently exported
 * @param propertyName - name of the property
 * @param flexList - the list to be enhanced
 */
function addFlexChangeInfoToList(
    propertyDefinition: Definition,
    value: ManifestSettingsType | undefined,
    propertyName: string,
    flexList: FlexInfo[]
) {
    const newValue = value === undefined ? null : value; // special logic for control properties which are not changed by flex
    const isBinding = typeof newValue === 'string' && newValue.startsWith('{');
    flexList.push({
        controlId: propertyDefinition[SchemaTag.controlId],
        controlType: propertyDefinition[SchemaTag.controlType],
        propertyName,
        newValue,
        isBinding
    });
}

/**
 * Returns how a value from the config should be passed to the manifest.
 * This function will be called for all properties with artifactType 'Manifest'.
 * In case the property has type 'object' it will also be recursively be called for all children of the object.
 *
 * @param value - the value for the property in the config currently processed (undefined if the property is not present in the config)
 * @param oldValue - current value for the property in the manifest (resp. undefined if the property is not in the current manifest)
 * @param propertyDefinition - definition of the property in the current schema
 * @param globalInfoForExport - global information valid for the export process
 * @returns the value for the property in the modified manifest, resp. undefined when the property should be removed from the manifest.
 */
function getValueForManifest(
    value: unknown,
    oldValue: unknown,
    propertyDefinition: Definition,
    globalInfoForExport: GlobalInfoForExport
): unknown {
    if (propertyDefinition[SchemaTag.conversionExit]) {
        // When a conversion exit has been defined for the property, let it do the job
        return executeConversionExit(
            false,
            globalInfoForExport.conversionExitProvider,
            propertyDefinition,
            value,
            globalInfoForExport.fragments,
            oldValue
        );
    }
    // Special logic for string arrays. Note that the opposite logic is implemented in function getConfigRep in import.importPage.ts
    if (propertyDefinition.type === 'array' && propertyDefinition.items?.['type'] === 'string') {
        if (!Array.isArray(value) || value.length === 0) {
            return oldValue === '' || typeof oldValue !== 'string' ? oldValue : undefined;
        }
        return value.join(',');
    }
    if (value === oldValue || propertyDefinition.type !== 'object' || !propertyDefinition.properties) {
        return value; // unchanged entries will not be modified. Moreover, only objects with properties need special care
    }
    // In case an object is to be returned this return object is built as follows:
    // Take (a shallow copy of) oldValue as a starting point (resp. an empty object if oldValue was not a valid object) and
    // replace its values for 'known' properties (i.e. the properties defined in the schema) by the corresponding property value from newValue.
    // If this would set the property value to undefined remove the property instead.
    // Note that this procedure guarantees that the values for unknown properties stay as they are in oldValue.
    const returnObject = typeof oldValue === 'object' && oldValue !== null ? { ...oldValue } : {};
    let hasNewValueDefinedChildProperty = false;
    let hasOldValueDefinedChildProperty = false;
    for (const childProperty in propertyDefinition.properties) {
        let childPropertyDefinition = propertyDefinition.properties[childProperty];
        if (!isDefinition(childPropertyDefinition)) {
            continue; // ignore properties defined as boolean (should not appear in our schemas anyway)
        }
        const refType = getRefType(childPropertyDefinition);
        childPropertyDefinition =
            (refType && globalInfoForExport.globalTypeDefinitions[refType]) || childPropertyDefinition;
        const oldChildValue = returnObject[childProperty];
        hasOldValueDefinedChildProperty = hasOldValueDefinedChildProperty || oldChildValue !== undefined;
        const newChildValue = getValueForManifest(
            value ? value[childProperty] : undefined,
            oldChildValue,
            childPropertyDefinition,
            globalInfoForExport
        );
        if (newChildValue === undefined) {
            delete returnObject[childProperty];
        } else {
            returnObject[childProperty] = newChildValue;
            hasNewValueDefinedChildProperty = true;
        }
    }
    // If the object which was built in the way described above has at least one known property value we can safely return it as the new value
    if (hasNewValueDefinedChildProperty) {
        return returnObject;
    }
    // When we reach this point newValue does not contain a value for any known property. If the same was true for oldValue we can safely return the oldValue as
    // no changes have been applied to this object.
    if (!hasOldValueDefinedChildProperty) {
        return oldValue;
    }
    // When we reach this point then newValue contains the information that all known properties should be removed from the oldValue
    // and that this has happened for at least one property. We still distinguish two cases:
    // - oldValues contains no unknown properties. As all known properties have been removed, returnObject has been stripped to be an empty object.
    //   In this case we return undefined to indicate that the object should be removed from the manifest as well.
    // - oldValue contains at least one unknown property. In this case we want to keep the object alive, now containing only the unknown properties.
    //   This is, what we get from returnObject.
    return Object.keys(oldValue).every((oldValueKey) => isDefinition(propertyDefinition.properties[oldValueKey]))
        ? undefined
        : returnObject;
}

/**
 * Returns whether two java script instances should be considered as different manifest entries.
 * Note that in this context two objects are already considered different when the order of their properties is not identical.
 * This is necessary, since for some manifest sections the order of its content has functional meaning.
 *
 * @param obj1 the first instance
 * @param obj2 the second instance
 * @returns whether obj1 and obj2 should be considered different.
 */
function isDifferent(obj1: unknown, obj2: unknown): boolean {
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
        return obj1 !== obj2;
    }
    // This if-block will probably not be applied to manifest sections directly, since manifests of v2 apps should not contain any
    // arrays. It is anyway needed for the comparison of the keys-arrays used below.
    if (Array.isArray(obj1) || Array.isArray(obj2)) {
        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            return (
                obj1.length !== obj2.length ||
                obj1.some(function (entry1, i) {
                    return isDifferent(entry1, obj2[i]);
                })
            );
        }
        return true; // one instance is an array, one is not.
    }
    const keys1 = Object.keys(obj1).filter((key) => obj1[key] !== undefined);
    const keys2 = Object.keys(obj2).filter((key) => obj2[key] !== undefined);
    return (
        isDifferent(keys1, keys2) ||
        keys1.some(function (key) {
            return isDifferent(obj1[key], obj2[key]);
        })
    );
}

/**
 * This function is called by transferConfigPropertyToExportResults (see below) for schema properties that have artifactType 'Manifest'.
 * It is responsible for transferring the config value of this property to the manifest and the fragments (not implemented yet).
 *
 * @param fullManifestPath - the path in the manifest (ending with the name of the property itself) the value should be passed to
 * @param value - content of the property according to the config, resp. undefined if the property is not present in the config.
 * In the later case the property should (normally) be removed from the manifest.
 * @param target - the results of the export to be updated
 * @param propertyDefinition - definition of the property in the page specific schema
 * @param globalInfoForExport - global information valid for the export process
 */
function applyManifestChange(
    fullManifestPath: AccessorPath,
    value: unknown,
    target: ExportPageResults,
    propertyDefinition: Definition,
    globalInfoForExport: GlobalInfoForExport
): void {
    let manifestSection: object = target.manifest; // will be used to step down in the hierarchy of the manifest to the parent of the target property
    const lengthToParent = fullManifestPath.length - 1;
    const finalProperty = fullManifestPath[lengthToParent]; // name of the property which should be updated
    const backPath: object[] = []; // Will be set to contain the (not null) objects in the current manifest which lie on the path to the parent of the target property
    // Step down in the manifest to the parent of the target property. Stop if an entry on that path is missing (or not an object).
    fullManifestPath.some(function (property, i) {
        if (i === lengthToParent) {
            // as we only want to navigate to the parent, stop now
            return true;
        }
        const nextSection = manifestSection[property];
        if (typeof nextSection !== 'object' || nextSection === null) {
            return true; // do not step down to something which is not an object (normally that means that we reached undefined, but incorrect manifests may also contain other values where an object is expected)
        }
        // Safely step down to the object on the next level
        manifestSection = nextSection;
        backPath.push(manifestSection);
    });
    // If the length of backPath is lengthToParent the parent object already exists in the manifest (and is an object). So we (potentially) have an old value for the target property
    const oldValue = backPath.length === lengthToParent ? manifestSection[finalProperty] : undefined;
    const newValue = getValueForManifest(value, oldValue, propertyDefinition, globalInfoForExport);
    if (!isDifferent(newValue, oldValue)) {
        return; // nothing to do
    }
    // newValue and oldValue are different
    if (newValue === undefined || (typeof oldValue === 'object' && typeof newValue === 'object' && isEmpty(newValue))) {
        // Two scenarios in which the target property should be removed from the manifest:
        // - newValue is undefined
        // - both oldValue and newValue are objects and newValue is empty (indicating that all properties have been removed)
        // Note: In both scenarios oldValue cannot be undefined (in the first since it is different from newValue). Hence, backPath.length === lengthToParent according to the definition of oldValue.
        target.manifestChangeIndicator = ChangeIndicator.Deleted;
        // Remove the specified property from its parent. Then loop the object hierarchy upwards and delete all objects which have been made empty by this process.
        for (let i = lengthToParent; i > 0; i--) {
            const candidateForRemove = backPath[i]; // will be undefined in the first run
            if (!isEmpty(candidateForRemove)) {
                break; // stop the process when finding an object that is not empty after deleting the specified entry. Will not happen in the first run.
            }
            // As the object is empty: Remove it from its parent. Will be executed at least once, thereby removing the specified property from its parent.
            const parent = backPath[i - 1];
            const prop = fullManifestPath[i];
            delete parent[prop];
        }
    } else {
        // Create a path of empty objects to the parent of target, if the manifest is not structured deep enough yet
        for (let i = backPath.length; i < lengthToParent; i++) {
            const newEmptyObject = {};
            manifestSection[fullManifestPath[i]] = newEmptyObject;
            manifestSection = newEmptyObject;
        }
        // Now manifestSection is the parent object of the property to be set -> update it with the new value
        manifestSection[finalProperty] = newValue;
        target.manifestChangeIndicator = oldValue === undefined ? ChangeIndicator.Created : ChangeIndicator.Updated;
    }
}

/**
 * This function is called by function transferConfigToExportResults (see below) for all properties contained in the
 * schema element processed by this function.
 * It is responsible for transferring the content of the config for this property to the export results.
 *
 * @param source - the section of the config that potentially contains the property
 * @param target - the results of the export to be updated
 * @param property - name of the property to be handled
 * @param propertyDefinition - definition of the property in the page specific schema
 * @param globalInfoForExport - global information valid for the export process
 */
function transferConfigPropertyToExportResults(
    source: unknown,
    target: ExportPageResults,
    property: string,
    propertyDefinition: Definition,
    globalInfoForExport: GlobalInfoForExport
): void {
    const sourcePart = source?.[property];
    const artifactType: string = propertyDefinition[SchemaTag.artifactType];
    // We distinguish three cases:
    // - current property is handled by flex
    // - current property can be mapped onto a manifest property (or a section within the manifest)
    // - property is still complex and points to a global type which describes how the content should be processed
    // Note that the inline definition of a complex type is not yet supported.
    if (artifactType === ArtifactType.FlexChange) {
        // Case 1
        addFlexChangeInfoToList(propertyDefinition, sourcePart, property, target.flexList);
    } else if (artifactType === ArtifactType.Manifest && propertyDefinition[SchemaTag.manifestPath]) {
        // Case 2
        // propertyDefinition.manifestPath is a JSONPath that describes how to find
        // the place in the manifest representing the property.
        // manifestPath contains the same information but in the more convenient AccessorPath format.
        const manifestPath = jsonPathToArray(propertyDefinition[SchemaTag.manifestPath] as string);
        applyManifestChange(manifestPath, sourcePart, target, propertyDefinition, globalInfoForExport);
    } else {
        // Note: inline type definitions currently not supported
        const refType = getRefType(propertyDefinition);
        const refTypeDefinition = refType ? globalInfoForExport.globalTypeDefinitions[refType] : undefined;
        if (isDefinition(refTypeDefinition)) {
            transferConfigToExportResults(sourcePart, target, refTypeDefinition, globalInfoForExport);
        }
    }
}

/**
 * This function is used by function exportPage (see below).
 * It (recursively) transfers the content of the config or one of its sub-objects to the export results.
 * Note that the process is driven by the structure of the schema and not by the structure of the current config.
 * This means that we will finally process all properties defined directly or indirectly within the given schema element, even if they are not contained in the config
 * (which means that no change for the corresponding property should be there).
 * This ensures that properties which have been removed from the config are handled correctly as well (for manifest based properties this means that they are removed from the manifest,
 * for flex changes this means that a flex change with value null is created).
 *
 * @param source - the section of the config to be transferred
 * @param target - the results of the export to be updated
 * @param definition - schema element describing the section of the config currently processed
 * @param globalInfoForExport - global information valid for the export process
 */
function transferConfigToExportResults(
    source: unknown,
    target: ExportPageResults,
    definition: Definition,
    globalInfoForExport: GlobalInfoForExport
): void {
    const properties = definition.properties || {};
    // Perform the update for all available properties
    for (const property in properties) {
        // propertyDefinition tells us how to handle the property
        const propertyDefinition = definition.properties[property];
        if (isDefinition(propertyDefinition)) {
            // ignore properties that are null or boolean (should not occur in our schemas anyway)
            transferConfigPropertyToExportResults(source, target, property, propertyDefinition, globalInfoForExport);
        }
    }
}

/**
 * This function implements the generic export for a page.
 * More precisely it takes the current state of the app (manifest, flex changes, fragments), the page specific schema for a page within the app, and the
 * config for this page. It returns the information how the app should be adapted.
 * Thereby, we assume that the config was created by function getConfigForPage (in importPage.ts) from the current state of the page
 * and then potentially modified (e.g. by user interaction).
 *
 * @param inParameters - an object containing all information relevant for the export process
 * @returns the information about the updated state of the app
 */
export function exportPage(inParameters: ExportPageInParameters): ExportPageResults {
    const config = inParameters.config;
    const globalTypeDefinitions = inParameters.jsonSchema.definitions as { [key: string]: Definition };

    // As a starting point create a return object representing the current state of the app
    const fragmentResults = (inParameters.fragments || []).map(function (fragment) {
        const result: FileDataResult = {
            ...fragment,
            changeIndicator: ChangeIndicator.NoChange
        };
        return result;
    });
    const viewResults = (inParameters.views || []).map(function (view) {
        const result: FileDataResult = {
            ...view,
            changeIndicator: ChangeIndicator.NoChange
        };
        return result;
    });
    const globalInfoForExport = {
        globalTypeDefinitions,
        conversionExitProvider: inParameters.conversionExitProvider,
        fragments: fragmentResults,
        views: viewResults
    };

    // TODO: Check whether property 'views' needs to be initialized as well
    const returnObject = {
        manifest: structuredClone(inParameters.manifest),
        manifestChangeIndicator: ChangeIndicator.NoChange,
        flexList: [] as FlexInfo[],
        fragments: fragmentResults,
        views: viewResults
    };
    // Adapt returnObject according to the state of the config. This is driven by the page specific schema.
    transferConfigToExportResults(config, returnObject, inParameters.jsonSchema, globalInfoForExport);
    return returnObject;
}
